/**
 * Settings utilities for developer toolbar
 * Handles localStorage persistence for toolbar configuration
 */
import logger from 'js/app/loggerSingleton';

import localStorageEx from 'bundles/common/utils/localStorageEx';

const STORAGE_PREFIX = 'courseraToolbar';

interface ToolbarSettings {
  isMinimized: boolean;
  position: {
    x: number | null;
    y: number | null;
  };
  bottomLocked: boolean;
  // Add other global settings here as needed
}

// Default settings
const defaultSettings: ToolbarSettings = {
  isMinimized: true,
  position: {
    x: null,
    y: null,
  },
  bottomLocked: false,
};

/**
 * Load all settings from localStorage
 */
export function loadSettings(): ToolbarSettings {
  try {
    const storedSettings = localStorageEx.getItem(`${STORAGE_PREFIX}.settings`, JSON.parse, null);
    if (storedSettings) {
      return { ...defaultSettings, ...storedSettings };
    }
  } catch (error) {
    logger.error('Error loading coursera toolbar settings:', error);
  }
  return { ...defaultSettings };
}

/**
 * Save all settings to localStorage
 */
export function saveSettings(settings: ToolbarSettings): void {
  try {
    localStorageEx.setItem(`${STORAGE_PREFIX}.settings`, settings, JSON.stringify);
  } catch (error) {
    logger.error('Error saving coursera toolbar settings:', error);
  }
}

/**
 * Update a specific setting
 */
export function updateSetting<K extends keyof ToolbarSettings>(key: K, value: ToolbarSettings[K]): void {
  const currentSettings = loadSettings();
  const updatedSettings = {
    ...currentSettings,
    [key]: value,
  };
  saveSettings(updatedSettings);
}

/**
 * Get a specific setting value
 */
export function getSetting<K extends keyof ToolbarSettings>(key: K): ToolbarSettings[K] {
  const settings = loadSettings();
  return settings[key];
}
